<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    const dispatch = createEventDispatcher()
    import { Purchase } from '../../modules/paymentsApi/purchases/purchase.entity'
    import dayjs from 'dayjs'
    import {
        PurchaseStatus,
        PurchaseStatusFriendly,
        PurchaseType,
        ScheduledCancelNextInstallmentFriendly,
    } from '../../modules/paymentsApi/purchases/purchase.enum'
    import Popover from '../_common/popover.svelte'
    import PaymentsCardInput from './payments-card-input.svelte'
    import { Charge } from '../../modules/paymentsApi/charges/charge.entity'
    import PaymentsChargeShow from './payments-charge-show.svelte'
    import { ChargeStatus, ChargeStatusFriendly, PaymentMethods, PaymentMethodsFriendly } from '../../modules/paymentsApi/charges/charge.enum'
    import Zframe from '../_common/z/zframe.svelte'
    import { DetailsClass, GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine, ZDetailItem } from '../_common/z/zgrid'
    import {
        faEye,
        faEdit,
        faQuestionCircle,
        faAddressCard,
        faMoneyBillWave,
        faCalendarAlt,
        faUser,
        faCreditCard,
        faPlug,
        faRightLeft,
        faPercent,
        faCalendarXmark,
        faBan,
    } from '@fortawesome/free-solid-svg-icons'
    import Zgrid from '../_common/z/zgrid.svelte'
    import Zdetails from '../_common/z/zdetails.svelte'
    import PaymentsPurchaseCustomer from './payments-purchase-customer.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import { Container } from 'sveltestrap'
    import PaymentsPurchaseDueDate from './payments-purchase-due-date.svelte'
    import Zdrop from '../_common/z/zdrop.svelte'
    import type { ZdropInterface } from '../_common/z/zdrop.interface'
    import PaymentsPurshaseValue from './payments-purshase-value.svelte'
    import PaymentsPurchaseScheduleCancel from './payments-purchase-schedule-cancel.svelte'
    import PaymentsPurchaseGateway from './payments-purchase-gateway.svelte'
    import PaymentsPurchaseMethod from './payments-purchase-method.svelte'
    import PaymentsPurchasePayer from './payments-purchase-payer.svelte'
    import PaymentsPurchaseAdjustment from './payments-purchase-adjustment.svelte'
    import type { Gateway } from '../../modules/paymentsApi/gateways/gateway.entity'

    export let purchaseId: string = null

    let purchase: Purchase = null

    // a assinatura só muda para 'cancelado' na data agendada. até lá ela segue ativa, e este é o
    // único sinal de que está para encerrar. depois de executar, scheduledCancelAt permanece
    // preenchido como histórico — por isso o status também entra na conta.
    $: isCancelling = !!purchase?.scheduledCancelAt && purchase?.status !== PurchaseStatus.canceled

    // O gateway é gravado por id na assinatura e em cada parcela; a lista serve só para exibir o
    // nome no lugar do uuid.
    //
    // A lista entra como parâmetro de propósito: sem ela na expressão do template o Svelte não sabe
    // que o texto depende de `gateways`, e o campo ficaria congelado no uuid da primeira renderização.
    let gateways: Gateway[] = []
    function gatewayLabel(gatewayId: string, lista: Gateway[]): string {
        if (!gatewayId) return null
        return lista?.find((el) => el.gatewayId === gatewayId)?.label || gatewayId
    }

    $: details = new DetailsClass({ items: purchase ? buildDetailItems(purchase, gateways) : [] })

    $: buyerDetailsConfig = new DetailsClass({
        items: purchase
            ? [
                  new ZDetailItem({ label: 'Pagador', content: purchase.card?.name, size: 4 }),
                  new ZDetailItem({ label: 'Cartão', content: '****.****.****.' + (purchase.card?.lastDigits || ''), size: 4 }),
                  new ZDetailItem({ label: 'Vencimento', content: purchase.card?.expirationMonth + '/' + purchase.card?.expirationYear, size: 4 }),
              ]
            : [],
    })

    function buildDetailItems(p: Purchase, gatewaysList: Gateway[]): ZDetailItem[] {
        const list: ZDetailItem[] = [
            new ZDetailItem({ label: 'Gateway', content: gatewayLabel(p.gatewayId, gatewaysList), size: 4 }),
            new ZDetailItem({
                label: 'Meio de pagamento',
                content:
                    p.purchaseMethod === PaymentMethods.credit
                        ? 'Cartão final ' + p.card.lastDigits
                        : // era 'Boleto' fixo: uma assinatura no pix aparecia como boleto
                          PaymentMethodsFriendly[p.purchaseMethod] || p.purchaseMethod,
                action: p.purchaseMethod === PaymentMethods.credit ? new GridButton({ icon: faQuestionCircle, callback: showBuyerDetails }) : null,
                title: p.card?.name || '',
                size: 4,
            }),
            new ZDetailItem({ label: 'Tipo de pagamento', content: friendlyType(p.purchaseType), size: 4 }),
            new ZDetailItem({ label: 'Cliente', content: p.person?.name || '[cliente não informado]', size: 4 }),
            new ZDetailItem({ label: 'ID', content: p.purchaseId, size: 4 }),
            new ZDetailItem({ label: 'Descrição', content: p.purchaseDescription, size: 4 }),
            new ZDetailItem({ label: 'Data da cobrança', type: GridColTypeEnum.datetime, content: p.createdAt, size: 4 }),
            new ZDetailItem({
                label: 'Status',
                type: GridColTypeEnum.badge,
                content: new GridBadge({ label: PurchaseStatusFriendly[p.statusLabel], class: getBadgeColor(p.status, 0) }),
                size: 4,
            }),
            new ZDetailItem({ label: 'Valor', content: window.api.utils.utils.toReal(p.purchaseTotalValue), size: 4 }),
        ]
        if (p.purchaseType !== PurchaseType.recurring_continuous) {
            list.push(new ZDetailItem({ label: 'Parcelamento', content: p.purchaseTotalInstallments + ' vez(es)', size: 4 }))
        }
        if (p.purchaseEntryValue) {
            // o rótulo diz o que a entrada é: o primeiro dos vencimentos, com valor próprio
            list.push(
                new ZDetailItem({
                    label: 'Entrada (1º vencimento)',
                    content: window.api.utils.utils.toReal(p.purchaseEntryValue),
                    size: 4,
                }),
            )
        }
        if (p.referenceId) {
            list.push(new ZDetailItem({ label: 'ID de referência', content: p.referenceId, size: 4 }))
        }
        return list
    }

    let show = true
    async function loadEntity() {
        show = true
        purchase = await window.api.payments.purchases.getOne(purchaseId, { invoices: true, card: true })
        purchase = await window.api.utils.utils.assignPersonsFromObject(purchase, 'referenceId')
        console.log('purchase', purchase)
        try {
            gateways = await window.api.payments.gateways.getAll()
        } catch (error) {
            // sem a lista a tela ainda funciona: cai no id
            gateways = []
        }
        loadMenu()
        loadGrid()
        show = false
    }

    onMount(async () => await loadEntity())

    function friendlyType(type: PurchaseType) {
        if (type === PurchaseType.full_charge) {
            return 'Crédito convencional'
        }
        if (type === PurchaseType.recurring_fixed) {
            return 'Recorrente fixo'
        }
        if (type === PurchaseType.recurring_continuous) {
            return 'Recorrente contínuo'
        }
    }

    let showChangeCard = false
    function goChangeCard() {
        showChangeCard = true
    }

    let showChangeCustomer = false
    function goChangeCustomer() {
        showChangeCustomer = true
    }

    let showChangeDueDate = false
    function goChangeDueDate() {
        showChangeDueDate = true
    }

    let showChangeValue = false
    function goChangeValue() {
        showChangeValue = true
    }

    let showChangeGateway = false
    function goChangeGateway() {
        showChangeGateway = true
    }

    let showPayer = false
    function goPayer() {
        showPayer = true
    }
    function closePayer() {
        showPayer = false
        loadEntity()
    }

    let showChangeMethod = false
    function goChangeMethod() {
        showChangeMethod = true
    }
    function closeChangeMethod() {
        showChangeMethod = false
        loadEntity()
    }

    // "Reajustar" abre a configuração do reajuste automático, e é de lá que sai o "Aplicar agora".
    // As duas coisas moram juntas porque são a mesma decisão: por qual índice este contrato corrige.
    let showAdjustment = false
    function goAdjustment() {
        showAdjustment = true
    }
    function closeAdjustment() {
        showAdjustment = false
        loadEntity()
    }

    function closeChangeGateway() {
        showChangeGateway = false
        loadEntity()
    }

    let showScheduleCancel = false
    function goScheduleCancel() {
        showScheduleCancel = true
    }

    function closeScheduleCancel() {
        showScheduleCancel = false
        loadEntity()
    }

    async function goRevokeScheduledCancel() {
        if (!confirm('Deseja manter esta assinatura ativa e desfazer o encerramento agendado?')) {
            return
        }
        try {
            show = true
            await window.api.payments.purchases.revokeScheduledCancel(purchaseId)
            await loadEntity()
        } catch (error) {
            show = false
            window.error(error.message || error)
        }
    }

    function closeCustomer() {
        showChangeCustomer = false
        loadEntity()
    }

    function closeDueDate() {
        showChangeDueDate = false
        loadEntity()
    }

    function closeValue() {
        showChangeValue = false
        loadEntity()
    }

    let selected: Charge = null
    function loadCharge(chargeId) {
        selected = purchase.charges.find((el) => el.chargeId === chargeId)
        console.log('selected charge', selected)
    }

    async function goCancelPurchase() {
        if (!confirm('Deseja mesmo CANCELAR esta cobrança?\nLembre-se: esta ação é irreversível.')) {
            return
        }
        await window.api.payments.purchases.update(purchaseId, { status: PurchaseStatus.canceled })
        dispatch('close')
    }

    function closeCharge() {
        selected = null
        loadEntity()
    }

    function getBadgeColor(status: ChargeStatus, tryTimes: number) {
        if (status === ChargeStatus.scheduled && tryTimes > 0) return GridBadgeClassEnum.badge_alert
        if (status === ChargeStatus.processing) return GridBadgeClassEnum.badge_alert
        if (status === ChargeStatus.canceled) return GridBadgeClassEnum.badge_alert
        if (status === ChargeStatus.paid) return GridBadgeClassEnum.badge_success
        if (status === ChargeStatus.paid_manual) return GridBadgeClassEnum.badge_success
        if (status === ChargeStatus.pending) return GridBadgeClassEnum.badge_alert
        // documento vencido: a dívida continua em aberto, então é aviso, não encerramento
        if (status === ChargeStatus.expired) return GridBadgeClassEnum.badge_alert
        if (status === ChargeStatus.declined) return GridBadgeClassEnum.badge_danger
        if (parseInt(status.toString()) === 120) return GridBadgeClassEnum.badge_alert
        if (parseInt(status.toString()) === 100) return GridBadgeClassEnum.badge_success
        if (parseInt(status.toString()) === 80) return GridBadgeClassEnum.badge_danger
        if (parseInt(status.toString()) === 5) return GridBadgeClassEnum.badge_danger
        if (parseInt(status.toString()) === 20) return GridBadgeClassEnum.badge_alert
        return null
    }

    function getFriendlyStatus(charge: Charge) {
        if (charge.status === ChargeStatus.scheduled && charge.tryTimes > 0) {
            return 'atrasado'
        }
        return ChargeStatusFriendly[charge.statusLabel]
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        grid = new GridClass()
        // grid head
        grid.title = 'Parcelas'
        grid.subtitle = ''
        grid.showActs = false
        grid.cols.push(new GridCol({ content: 'Vencimento', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Pago em', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2 }))
        // cada parcela é cobrada no gateway em que foi enviada, que pode não ser o atual da
        // assinatura depois de uma troca — por isso a coluna é por parcela, não só no cabeçalho
        grid.cols.push(new GridCol({ content: 'Gateway', type: GridColTypeEnum.string, size: 2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        for (const charge of purchase.charges) {
            let badge: GridBadge = new GridBadge({ label: getFriendlyStatus(charge), class: getBadgeColor(charge.status, charge.tryTimes) })
            let open: GridButton = new GridButton({
                label: 'Abrir parcela',
                icon: faEdit,
                callback: () => loadCharge(charge.chargeId),
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: charge.dueDate })
            line.slots.push({ content: badge })
            line.slots.push({ content: charge.paidAt })
            line.slots.push({ content: charge.value })
            // parcela ainda não enviada não pertence a gateway nenhum: ela só é fixada na cobrança
            line.slots.push({ content: gatewayLabel(charge.gatewayId, gateways) || 'a definir' })
            line.slots.push({ content: [open] })
            grid.lines.push(line)
        }
    }

    async function updateBillIntegration() {
        await window.api.payments.purchases.billIntegration(purchase.purchaseId, {
            billIntegration: purchase.billIntegration,
        })
    }

    let buyerDetails = false
    function showBuyerDetails() {
        buyerDetails = true
    }

    let menu: ZdropInterface = {
        format: 'square',
        position: 'flow',
        positionMargin: '4px',
        buttons: [],
    }

    // O menu é montado inteiro aqui, e não na declaração, porque quase toda condição depende do
    // purchase — que ainda não foi carregado naquele momento.
    function loadMenu() {
        const isCard = purchase?.purchaseMethod === PaymentMethods.credit
        // Os ícones separam o que cada item alcança: a ficha do pagador (faAddressCard) não se
        // confunde com trocar a pessoa da cobrança (faUser), e o provedor de pagamento usa o mesmo
        // faPlug das integrações, enquanto o meio de pagamento usa a seta de troca.
        const buttons: ZdropInterface['buttons'] = [
            // o cadastro do pagador é o dado que sustenta toda cobrança: vem antes das alterações
            { callback: goPayer, label: 'Dados do pagador', icon: faAddressCard },
            { callback: goChangeValue, label: 'Alterar Valor', icon: faMoneyBillWave },
            { callback: goChangeDueDate, label: 'Alterar vencimento', icon: faCalendarAlt },
            { callback: goChangeCustomer, label: 'Alterar cliente', icon: faUser },
            // trocar cartão só faz sentido em assinatura de cartão
            { disabled: !isCard, callback: goChangeCard, label: 'Alterar cartão', icon: faCreditCard },
            // e trocar de gateway é o oposto: o token do cartão não atravessa para outro gateway,
            // então cartão é justamente quem não pode
            { disabled: isCard, callback: goChangeGateway, label: 'Alterar gateway', icon: faPlug },
        ]

        // Trocar o meio só vale para recorrente. Numa cobrança à vista no cartão o valor pode
        // estar parcelado pela adquirente, e pix e boleto não parcelam: a troca viraria um
        // pagamento único do valor cheio. O backend recusa full_charge pelo mesmo motivo.
        if (purchase?.purchaseType !== PurchaseType.full_charge) {
            buttons.push({ callback: goChangeMethod, label: 'Alterar meio de pagamento', icon: faRightLeft })
        }

        // Reajuste por índice só existe em assinatura: é o único tipo que cobra por período
        // indefinido e por isso envelhece. Carnê e à vista têm total fechado na venda.
        if (purchase?.purchaseType === PurchaseType.recurring_continuous) {
            buttons.push({ callback: goAdjustment, label: 'Reajustar', icon: faPercent })
        }

        // 'Agendar cancelamento' só existe para recorrente contínuo, e some enquanto já houver um
        // agendamento em aberto — nesse caso a ação disponível é desfazer, no aviso acima da tela.
        if (purchase?.purchaseType === PurchaseType.recurring_continuous && !purchase?.scheduledCancelAt) {
            buttons.push({ callback: goScheduleCancel, label: 'Agendar cancelamento', icon: faCalendarXmark })
        }

        buttons.push({ callback: goCancelPurchase, label: 'Cancelar cobrança', icon: faBan })
        menu = { ...menu, buttons }
    }
</script>

{#if showChangeCard}
    <Popover
        title="Atualizar cartão"
        width="780px"
        height="520px"
        on:close={() => {
            showChangeCard = false
            loadEntity()
        }}
    >
        <PaymentsCardInput {purchaseId} />
    </Popover>
{/if}

{#if showChangeCustomer}
    <Popover
        title="Atualizar cliente"
        width="780px"
        height="320px"
        on:close={() => {
            closeCustomer()
        }}
    >
        <PaymentsPurchaseCustomer {purchaseId} on:close={closeCustomer} />
    </Popover>
{/if}

{#if showChangeDueDate}
    <Popover
        title="Alterar vencimento"
        width="780px"
        height="320px"
        on:close={() => {
            closeDueDate()
        }}
    >
        <PaymentsPurchaseDueDate {purchaseId} on:close={closeDueDate} />
    </Popover>
{/if}

{#if showChangeValue}
    <Popover
        title="Alterar valor"
        width="780px"
        height="320px"
        on:close={() => {
            closeValue()
        }}
    >
        <PaymentsPurshaseValue {purchaseId} on:close={closeValue} />
    </Popover>
{/if}

{#if showAdjustment}
    <Popover title="Reajuste de preço" width="880px" height="640px" on:close={closeAdjustment}>
        <PaymentsPurchaseAdjustment {purchaseId} on:applied={loadEntity} />
    </Popover>
{/if}

{#if showChangeGateway}
    <Popover
        title="Alterar gateway"
        width="780px"
        height="420px"
        on:close={() => {
            closeChangeGateway()
        }}
    >
        <PaymentsPurchaseGateway {purchaseId} on:close={closeChangeGateway} />
    </Popover>
{/if}

{#if showPayer}
    <Popover
        title="Dados do pagador"
        width="860px"
        height="560px"
        on:close={() => {
            closePayer()
        }}
    >
        <PaymentsPurchasePayer {purchaseId} on:close={closePayer} />
    </Popover>
{/if}

{#if showChangeMethod}
    <Popover
        title="Alterar meio de pagamento"
        width="780px"
        height="420px"
        on:close={() => {
            closeChangeMethod()
        }}
    >
        <PaymentsPurchaseMethod {purchaseId} on:close={closeChangeMethod} />
    </Popover>
{/if}

{#if showScheduleCancel}
    <Popover
        title="Agendar cancelamento"
        width="780px"
        height="360px"
        on:close={() => {
            closeScheduleCancel()
        }}
    >
        <PaymentsPurchaseScheduleCancel {purchaseId} on:close={closeScheduleCancel} />
    </Popover>
{/if}

{#if selected}
    <Popover title="Parcela" width="1100px" on:close={closeCharge}>
        <PaymentsChargeShow personId={purchase.person?.personId} purchaseStatus={purchase.status} charge={selected} on:close={closeCharge} />
    </Popover>
{/if}

{#if buyerDetails}
    <Popover title="Detalhes do cartão" width="760px" height="100px" on:close={() => (buyerDetails = false)}>
        <Zframe class="m-3" title="Dados do cartão" headline="">
            <Zdetails details={buyerDetailsConfig} />
        </Zframe>
    </Popover>
{/if}

{#if !show}
    <Container>
        <div class="root purchase-show">
            <div class="">
                <Zact class="h-space">
                    {#if window.store.token.permissions.pay.entries.write}
                        <div class="form-check form-switch">
                            <input
                                class="form-check-input"
                                bind:checked={purchase.billIntegration}
                                type="checkbox"
                                role="switch"
                                id="consulting"
                                on:change={updateBillIntegration}
                            />
                            <label class="form-check-label" for="consulting">Lançar parcelas no 'Contas a Receber'</label>
                        </div>

                        {#if purchase.status > PurchaseStatus.canceled}
                            <div class="spacer" />
                            <Zdrop {menu}></Zdrop>
                        {/if}
                    {/if}
                </Zact>
                {#if isCancelling}
                    <div class="alert alert-warning h-space schedule-cancel-alert">
                        <div>
                            Esta assinatura está agendada para encerrar em
                            <strong>{dayjs(purchase.scheduledCancelAt).format('DD/MM/YYYY')}</strong>. Até lá ela continua ativa.
                        </div>
                        {#if ScheduledCancelNextInstallmentFriendly[purchase.scheduledCancelNextInstallment]}
                            <div class="schedule-cancel-detail">
                                Próxima cobrança: {ScheduledCancelNextInstallmentFriendly[purchase.scheduledCancelNextInstallment]}.
                            </div>
                        {/if}
                        {#if window.store.token.permissions.pay.entries.write}
                            <div class="mt-2">
                                <Zbutton label="Desfazer cancelamento" color="primary" on:click={goRevokeScheduledCancel}></Zbutton>
                            </div>
                        {/if}
                    </div>
                {/if}
                <!-- {#if purchase.status === PurchaseStatus.canceled}
                <div class="row">
                    <div class="col">
                        <div class="alert alert-warning mx-2 text-center">Esta cobrança foi cancelada</div>
                    </div>
                </div>
            {/if} -->
                <Zframe class="h-space" title="Dados da Cobrança" headline="">
                    <Zdetails {details} />
                </Zframe>
            </div>
            <div class="h-space mb-2">
                <Zgrid {grid} />
            </div>
        </div>
    </Container>
{/if}

<style>
    .schedule-cancel-alert {
        margin-bottom: 0;
    }
    .schedule-cancel-detail {
        margin-top: 4px;
        opacity: 0.85;
    }
</style>
